import { useState, useEffect, useCallback } from "react";
import {
  initOfflineQueue,
  isBackgroundSyncAvailable,
  queueMessage as queueMsg,
  getPendingMessages,
  retryPendingMessages,
  deletePendingMessage,
  subscribeToPendingMessages,
  setupConnectionListeners
} from "../utils/offlineMessageQueue";

/**
 * Hook para manejar la cola de mensajes offline
 */
export function useOfflineQueue() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Inicializar cuando el SW esté listo
  useEffect(() => {
    let unsubscribe;
    let unsubscribeConnection;
    
    const init = async () => {
      if ("serviceWorker" in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          initOfflineQueue(registration);
          setIsInitialized(true);
          
          // Suscribirse a cambios en mensajes pendientes
          unsubscribe = subscribeToPendingMessages(setPendingMessages);
          
          // Suscribirse a cambios de conexión
          unsubscribeConnection = setupConnectionListeners();
        } catch (err) {
          console.error("[useOfflineQueue] Init failed:", err);
        }
      }
    };
    
    init();
    
    // Escuchar cambios de conexión
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    
    return () => {
      if (unsubscribe) unsubscribe();
      if (unsubscribeConnection) unsubscribeConnection();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);
  
  // Encolar mensaje
  const handleQueueMessage = useCallback(async (contactId, message, contactName) => {
    const token = localStorage.getItem("token") || undefined;
    const result = await queueMsg(contactId, message, contactName, token);
    return result.success;
  }, []);
  
  // Reintentar todos
  const retryAll = useCallback(async () => {
    await retryPendingMessages();
  }, []);
  
  // Eliminar mensaje
  const deleteMessage = useCallback(async (id) => {
    await deletePendingMessage(id);
  }, []);
  
  // Refrescar lista
  const refresh = useCallback(async () => {
    const messages = await getPendingMessages();
    setPendingMessages(messages);
  }, []);
  
  return {
    isOnline,
    pendingMessages,
    pendingCount: pendingMessages.filter(m => m.status === "pending").length,
    isBackgroundSyncSupported: isBackgroundSyncAvailable(),
    queueMessage: handleQueueMessage,
    retryAll,
    deleteMessage,
    refresh
  };
}

export default useOfflineQueue;
